import React, { useState, useEffect } from 'react'
import { css } from 'glamor'
import { useQuery } from '@apollo/client'
import * as theme from '../../theme'
import { CardContainer } from '../../components/Container'
import { H1 } from '../../components/Typography'
import { Table, TBody, THead } from '../../components/Table'
import { Loading } from '../../components/LoadingFork'
import { CellPill } from '../../components/StatusPill'
import { HollowButton } from '../../components/Button'
import { withInstallation } from '../../components/InstallationContext'
import { isQrEntryExitEnabled } from '../../lib/qrFeature'
import drivoApollo from '../../lib/drivoApollo'
import { consoleQrCodesByInstallation } from './queries'

const headerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
})

const statsRow = css({
  display: 'flex',
  gap: 12,
  marginBottom: 20,
  flexWrap: 'wrap',
})

const statBadge = (color) => css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 'bold',
  background: 'rgba(156,159,194,.1)',
  color: color,
})

const filterRow = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 16,
  gap: 12,
})

const searchInput = css({
  flex: 1,
  maxWidth: 400,
  padding: '8px 12px',
  border: `1px solid ${theme.gray30}`,
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
  ':focus': {
    borderColor: theme.blue,
  },
})

const filterBtnGroup = css({
  display: 'flex',
  gap: 4,
})

const filterBtn = (active) => css({
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 'bold',
  border: `1px solid ${theme.gray30}`,
  cursor: 'pointer',
  background: active ? theme.green : theme.white,
  color: active ? theme.white : theme.gray60,
  transition: 'all 0.2s',
  ':hover': {
    background: active ? theme.green : theme.gray10,
  },
})

const paginationRow = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 16,
  padding: '10px 0',
})

const rowStyle = css({
  '> td': {
    verticalAlign: 'middle !important',
    height: '50px !important',
    borderBottom: `1px solid ${theme.gray10} !important`,
    textAlign: 'center !important',
    fontSize: '12px !important',
    padding: '8px 8px !important',
    whiteSpace: 'nowrap',
  },
  ':hover > td': {
    background: theme.gray10,
  },
})

const headStyle = css({
  '> th': {
    textAlign: 'center !important',
    fontSize: '11px !important',
    fontWeight: 'bold !important',
    color: `${theme.gray50} !important`,
    textTransform: 'uppercase',
    padding: '12px 8px !important',
    borderBottom: `2px solid ${theme.gray30} !important`,
    whiteSpace: 'nowrap',
  },
})

const STATUS_COLORS = {
  ACTIVE: theme.green,
  USED: theme.blue,
  EXPIRED: theme.orange,
  REVOKED: theme.red,
}

const pageBtn = (active) => css({
  padding: '6px 10px',
  minWidth: 32,
  borderRadius: 4,
  fontSize: 13,
  fontWeight: active ? 'bold' : 'normal',
  border: `1px solid ${active ? theme.green : theme.gray30}`,
  cursor: 'pointer',
  background: active ? theme.green : theme.white,
  color: active ? theme.white : theme.gray60,
  transition: 'all 0.2s',
  ':hover': {
    background: active ? theme.green : theme.gray10,
  },
})

export function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = []
  pages.push(1)
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i)
  }
  if (current < total - 2) pages.push('...')
  pages.push(total)
  return pages
}

const STATUSES = ['ALL', 'ACTIVE', 'USED', 'EXPIRED', 'REVOKED']

export function formatDate(val) {
  if (!val) return '—'
  /* API serializes Date fields as string epoch-milliseconds (verified against
   * the qrCodesByInstallation resolvers), with ISO strings as a fallback */
  const ts = /^\d+$/.test(val) ? parseInt(val, 10) : val
  const d = new Date(ts)
  if (isNaN(d.getTime())) return '—'
  const day = d.getDate()
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  const hours = String(d.getHours()).padStart(2, '0')
  const mins = String(d.getMinutes()).padStart(2, '0')
  return `${day} ${month} ${year}, ${hours}:${mins}`
}

function QrCodesPage({ installation }) {
  const installationId = installation?.id
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [page, setPage] = useState(1)
  const pageSize = 20

  /* Debounce search input */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data, loading, error, refetch } = useQuery(consoleQrCodesByInstallation, {
    client: drivoApollo,
    variables: {
      installationId,
      page,
      pageSize,
      status: statusFilter === 'ALL' ? null : statusFilter,
      search: debouncedSearch || null,
    },
    skip: !installationId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const result = data?.consoleQrCodesByInstallation
  const qrCodes = result?.items || []
  const total = result?.total || 0
  const totalPages = result?.totalPages || 1
  const stats = result?.stats || { total: 0, active: 0, used: 0, expired: 0, revoked: 0 }

  const handleStatusChange = (status) => {
    setStatusFilter(status)
    setPage(1)
  }

  if (error) {
    return (
      <CardContainer style={{ padding: 30 }}>
        <div {...headerStyle}>
          <H1>QR Entry/Exit</H1>
          <HollowButton onClick={() => refetch()}>Retry</HollowButton>
        </div>
        <div style={{ color: theme.red, fontSize: 14 }}>
          Failed to load QR codes: {error.message}
        </div>
      </CardContainer>
    )
  }

  return (
    <CardContainer style={{ padding: 30 }}>
      <div {...headerStyle}>
        <H1>QR Entry/Exit</H1>
        <HollowButton onClick={() => refetch()} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </HollowButton>
      </div>

      {/* Stats badges */}
      <div {...statsRow}>
        <span {...statBadge(theme.gray60)}>{stats.total} Total</span>
        <span {...statBadge(STATUS_COLORS.ACTIVE)}>{stats.active} Active</span>
        <span {...statBadge(STATUS_COLORS.USED)}>{stats.used} Used</span>
        <span {...statBadge(STATUS_COLORS.EXPIRED)}>{stats.expired} Expired</span>
        <span {...statBadge(STATUS_COLORS.REVOKED)}>{stats.revoked} Revoked</span>
      </div>

      {/* Search + Filter */}
      <div {...filterRow}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
          <input
            {...searchInput}
            type="text"
            placeholder="Search by code or plate..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <span
              onClick={() => setSearch('')}
              style={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: theme.gray50,
                fontSize: 16,
                fontWeight: 'bold',
              }}
            >
              ✕
            </span>
          )}
        </div>
        <div {...filterBtnGroup}>
          {STATUSES.map((s) => (
            <button
              key={s}
              {...filterBtn(statusFilter === s)}
              onClick={() => handleStatusChange(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div style={{ color: theme.gray50, fontSize: 13, marginBottom: 10 }}>
        {total} QR codes found
        {debouncedSearch && ` matching "${debouncedSearch}"`}
      </div>

      {/* Full-screen loader only for the very first load — refetches keep the
        * table mounted so filter/pagination controls don't jump around */}
      {loading && !result && <Loading />}

      {result && qrCodes.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: theme.gray40, fontSize: 14 }}>
          {debouncedSearch || statusFilter !== 'ALL'
            ? 'No QR codes match the current search/filter.'
            : 'No QR codes found.'}
        </div>
      )}

      {qrCodes.length > 0 && (
        <>
          <Table>
            <THead>
              <tr {...headStyle}>
                <th>Code</th>
                <th>Plate</th>
                <th>Status</th>
                <th>Valid From</th>
                <th>Valid Until</th>
                <th>Entry Scan</th>
                <th>Exit Scan</th>
                <th>Created</th>
              </tr>
            </THead>
            <TBody>
              {qrCodes.map((qr) => (
                <tr key={qr.id} {...rowStyle}>
                  <td style={{ fontWeight: 'bold', fontFamily: 'monospace' }}>{qr.code}</td>
                  <td>{qr.plate || '—'}</td>
                  <td>
                    <CellPill color={STATUS_COLORS[qr.status] || theme.gray50} width={70}>
                      {qr.status}
                    </CellPill>
                  </td>
                  <td>{formatDate(qr.validFrom)}</td>
                  <td>{formatDate(qr.validUntil)}</td>
                  <td>{formatDate(qr.entryScannedAt)}</td>
                  <td>{formatDate(qr.exitScannedAt)}</td>
                  <td>{formatDate(qr.createdAt)}</td>
                </tr>
              ))}
            </TBody>
          </Table>

          {/* Pagination */}
          <div {...paginationRow}>
            <span style={{ fontSize: 13, color: theme.gray50 }}>
              Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
            </span>
            <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                {...pageBtn(false)}
                onClick={() => setPage(1)}
                disabled={page <= 1}
                style={{ opacity: page <= 1 ? 0.4 : 1 }}
              >
                «
              </button>
              <button
                {...pageBtn(false)}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ opacity: page <= 1 ? 0.4 : 1 }}
              >
                ‹
              </button>
              {getPageNumbers(page, totalPages).map((p, i) => (
                p === '...' ? (
                  <span key={`dot-${i}`} style={{ padding: '0 4px', color: theme.gray40 }}>...</span>
                ) : (
                  <button
                    key={p}
                    {...pageBtn(page === p)}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                )
              ))}
              <button
                {...pageBtn(false)}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ opacity: page >= totalPages ? 0.4 : 1 }}
              >
                ›
              </button>
              <button
                {...pageBtn(false)}
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                style={{ opacity: page >= totalPages ? 0.4 : 1 }}
              >
                »
              </button>
            </div>
          </div>
        </>
      )}
    </CardContainer>
  )
}

/* The sidebar only links here for QR-enabled hotels, but a bookmarked or
   hand-typed URL would still render the page for a hotel that has the feature
   switched off in Admin. Guard the route the same way Insights does, in a
   wrapper so the page's own hooks are never called conditionally. */
export function QrCodesRoute({ installation, facility }) {
  if (!isQrEntryExitEnabled(facility)) return null

  return <QrCodesPage installation={installation} />
}

export default withInstallation(QrCodesRoute)
